
import { Button } from "@/components/ui/button";
import { getActivityIcon, getPriorityColor, isValidUuid } from "./activityUtils";
import { Badge } from "@/components/ui/badge";
import type { Activity } from "./ActivityTypes";
import { Check } from "lucide-react";
import React from "react";
import { toast } from "sonner";

/** UI for rendering an individual activity, with complete/minimize states. */
interface ActivityItemProps {
  activity: Activity;
  onStart: (a: Activity) => void;
  onComplete: (a: Activity) => void;
  isMutationPending: boolean;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  activity,
  onStart,
  onComplete,
  isMutationPending,
}) => {
  // If completed: show only the title, completed badge, and a check icon
  if (activity.status === "completed") {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border-l-4 border-green-400 mb-1">
        <Check className="h-4 w-4 text-green-600 shrink-0" aria-label="Completed" />
        <span className="font-semibold text-green-700 line-clamp-1 flex-1">{activity.title}</span>
        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
          Completed
        </Badge>
      </div>
    );
  }

  // Check if related entity has a valid ID
  const hasInvalidRelatedEntity = activity.related_entity && 
    !isValidUuid(activity.related_entity.id);

  // Handle starting an activity with validation
  const handleStart = () => {
    if (hasInvalidRelatedEntity) {
      toast.error(`Cannot start activity: Invalid ${activity.related_entity?.type} ID format`);
      return;
    }
    onStart(activity);
  };

  // If not completed: show full card with actions for pending/in_progress
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
      <div className="mt-1 text-primary">{getActivityIcon(activity.type)}</div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{activity.title}</h4>
          {activity.time && (
            <span className="text-xs text-muted-foreground">{activity.time}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{activity.description}</p>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${getPriorityColor(activity.priority)}`}>
            {activity.priority.charAt(0).toUpperCase() + activity.priority.slice(1)} Priority
          </span>
          {activity.related_entity && (
            <span className="text-xs bg-secondary/50 px-2 py-0.5 rounded-full">
              {activity.related_entity.name}
              {hasInvalidRelatedEntity && (
                <span className="text-xs text-red-500 ml-1">(Invalid ID)</span>
              )}
            </span>
          )}
        </div>
      </div>
      {activity.status === "pending" && (
        <Button
          variant="ghost"
          size="sm"
          disabled={isMutationPending || hasInvalidRelatedEntity}
          onClick={handleStart}
          title={hasInvalidRelatedEntity ? "This activity has an invalid entity ID" : undefined}
        >
          Start
        </Button>
      )}
      {activity.status === "in_progress" && (
        <Button
          variant="default"
          size="sm"
          disabled={isMutationPending}
          className="bg-green-600 hover:bg-green-700 text-white"
          onClick={() => onComplete(activity)}
        >
          Mark Complete
        </Button>
      )}
    </div>
  );
};
